import React, { FC } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import user2 from 'assets/img/icons/ic-user.svg';
import user3 from 'assets/img/icons/ic-edit.svg';
import user4 from 'assets/img/icons/ic-billing.svg';
import user6 from 'assets/img/icons/ic-help.svg';
import user7 from 'assets/img/icons/ic-paymentmethod.svg';

import { useSelector } from 'react-redux';
import i18next from 'i18next';
import { Button, Grid, Menu, MenuItem, Typography } from '@material-ui/core';
import { INFLUENCER_ONLY_MODE, HIDE_MOBILE_LOGIN } from '../../../../environment/environment';
import { logoutUser } from '../../../../store/actions/CurrentUser.actions';
import { CurrentUserReducer, RootReducer } from '../../../../models/Redux';
import { CurrentUser } from '../../../../models/User';
import { useStyles } from './styles';
import useIsUserLoggedIn from '../../../../config/hooks/useIsUserLoggedIn';

interface Props {
    isMobile?: boolean;
    referral?: boolean;
}

const LoginAndUserMenu: FC<Props> = ({ isMobile, referral }) => {
    const history = useHistory();
    const isUserLoggedIn = useIsUserLoggedIn();
    const classes = useStyles();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const { user, isInfluencer, loading: loadingUserInfo } = useSelector<RootReducer, CurrentUserReducer>(
        state => state.currentUser,
    );

    const handleMenuButtonClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuButtonClose = () => setAnchorEl(null);

    const logout = async (): Promise<void> => {
        await logoutUser();

        const { href } = window.location;
        if (href.charAt(href.length - 1) === '/') {
            window.location.reload();
        } else {
            history.push('/');
        }
    };

    const isInfluencerRequestPending = (user: Partial<CurrentUser>) =>
        user.influencer_request &&
        !user.influencer_request?.influencer_request_authorized &&
        user.user_role === 0;

    const isNotInfluencerAndHasNotRequested = (user: Partial<CurrentUser>) =>
        user.user_role === 0 && !user.influencer_request;

    const hasInfluencerRole = (user: Partial<CurrentUser>) => user.user_role === 1;

    const isNotLoggedInAndViewerModeEnabled = () =>
        !loadingUserInfo && !referral && !INFLUENCER_ONLY_MODE && !(isMobile && HIDE_MOBILE_LOGIN);

    const isNotLoggedInAndInfluencerModeOnly = () => !loadingUserInfo && INFLUENCER_ONLY_MODE;

    const renderInfluencerButton = () => {
        return (
            <>
                {isNotInfluencerAndHasNotRequested(user) ? (
                    <Button
                        className={clsx(classes.loginButton, classes.influencerButtons)}
                        data-toggle="modal"
                        data-target="#myModal_verify_influencer"
                    >
                        {i18next.t('LoginMenuComponentBecomeInfluencer')}
                    </Button>
                ) : isInfluencerRequestPending(user) ? (
                    <Button className={clsx(classes.loginButton, classes.influencerButtons)}>
                        {i18next.t('LoginMenuComponentProcessingInfluencerRequest')}
                    </Button>
                ) : (
                    hasInfluencerRole(user) && (
                        <Button className={clsx(classes.loginButton, classes.influencerButtons)}>
                            {i18next.t('LoginMenuComponentGoToInfluencerMode')}
                        </Button>
                    )
                )}
            </>
        );
    };

    const renderLoggedInUserMenuOptions = () => (
        <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuButtonClose}
            onClick={handleMenuButtonClose}
            PaperProps={{ className: classes.menu }}
        >
            <MenuItem component={NavLink} to="/profile" key="profile" className={classes.menuItem}>
                <img className={classes.menuItemIcon} src={user2} alt="" />{' '}
                {i18next.t('LoginMenuComponentMyViewerProfile')}
            </MenuItem>
            <MenuItem
                component={NavLink}
                to="/viewer_profile"
                key="viewer_profile"
                className={classes.menuItem}
            >
                <img src={user3} className={classes.menuItemIcon} alt="" />{' '}
                {i18next.t('LoginMenuComponentEditProfile')}
            </MenuItem>
            <MenuItem
                component={NavLink}
                to="/billing_viewer"
                key="billing_viewer"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user4} alt="" />{' '}
                {i18next.t('LoginMenuComponentBilling')}
            </MenuItem>
            <MenuItem
                component={NavLink}
                to="/my_viewer_payment_methods"
                key="my_viewer_payment_methods"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user7} alt="" />{' '}
                {i18next.t('LoginMenuComponentPaymentMethods')}
            </MenuItem>
            <MenuItem
                component="a"
                target="_blank"
                href="https://support.gotmy.com/hc/en-us"
                key="help"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user6} alt="" />{' '}
                {i18next.t('LoginMenuComponentHelp')}
            </MenuItem>

            {renderInfluencerButton()}

            <MenuItem
                component={Button}
                onClick={logout}
                key="logout"
                className={clsx(classes.logoutButton, classes.menuItem)}
            >
                <Typography className={classes.logoutButtonLabel}>
                    {i18next.t('LoginMenuComponentLogout')}
                </Typography>
            </MenuItem>
        </Menu>
    );

    const renderInfluencerMenuOptions = () => (
        <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuButtonClose}
            onClick={handleMenuButtonClose}
            PaperProps={{ className: classes.menu }}
        >
            <MenuItem
                component={NavLink}
                to="/profile_influencer"
                key="profile_influencer"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user2} alt="" />{' '}
                {i18next.t('InfluencerDropdownMenuMyProfile')}
            </MenuItem>
            <MenuItem
                component={NavLink}
                to="/influencer_profile/active"
                key="influencer_profile/active"
                className={classes.menuItem}
            >
                <img src={user3} className={classes.menuItemIcon} alt="" />{' '}
                {i18next.t('LoginMenuComponentEditProfile')}
            </MenuItem>

            <MenuItem
                component={NavLink}
                to="/my_live_events"
                key="my_live_events"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user2} alt="" />{' '}
                {i18next.t('InfluencerDropdownMenuMyLiveEvents')}
            </MenuItem>
            <MenuItem component={NavLink} to="/my_balance" key="my_balance" className={classes.menuItem}>
                <img className={classes.menuItemIcon} src={user4} alt="" />{' '}
                {i18next.t('InfluencerDropdownMenuMyBalance')}
            </MenuItem>
            <MenuItem
                component={NavLink}
                to="/my_influencer_payout_methods"
                key="my_influencer_payout_methods"
                className={classes.menuItem}
            >
                <img className={classes.menuItemIcon} src={user7} alt="" />{' '}
                {i18next.t('InfluencerDropdownMenuMyPayoutMethods')}
            </MenuItem>

            <Button className={clsx(classes.loginButton, classes.influencerButtons)}>
                {i18next.t('InfluencerDropdownGoViewer')}
            </Button>

            <MenuItem
                component={Button}
                onClick={logout}
                key="logout"
                className={clsx(classes.logoutButton, classes.menuItem)}
            >
                <Typography className={classes.logoutButtonLabel}>
                    {i18next.t('LoginMenuComponentLogout')}
                </Typography>
            </MenuItem>
        </Menu>
    );

    const renderLoggedInUserMenu = () => (
        <>
            <Grid container onClick={handleMenuButtonClick} className={classes.dropdownButton}>
                <Grid item direction="column" justify="space-around">
                    <Typography className={classes.name}>
                        {user.user_name !== '' ? user.user_name : user.user_username}
                    </Typography>
                    <Typography className={classes.mode}>
                        {i18next.t(
                            isInfluencer
                                ? 'InfluencerDropdownMenuInfluencerMode'
                                : 'LoginMenuComponentViewerMode',
                        )}
                    </Typography>
                </Grid>
                <Grid item className={classes.avatarContainer}>
                    <img
                        alt=""
                        className={classes.avatar}
                        src={
                            user.user_avatar ||
                            'https://gotmy-general-assets.s3.amazonaws.com/img/user/generic.png'
                        }
                    />
                </Grid>
            </Grid>
            {isInfluencer ? renderInfluencerMenuOptions() : renderLoggedInUserMenuOptions()}
        </>
    );

    return isUserLoggedIn ? (
        renderLoggedInUserMenu()
    ) : isNotLoggedInAndViewerModeEnabled() ? (
        <Button className={classes.loginButton} data-toggle="modal" data-target="#myModal_login">
            {isMobile
                ? i18next.t('LoginMenuComponentMobileSignup')
                : i18next.t('LoginMenuComponentMobileLogin')}
        </Button>
    ) : isNotLoggedInAndInfluencerModeOnly() ? (
        <Button className={classes.loginButton}>{i18next.t('LoginMenuComponentLoginAsInfluencer')}</Button>
    ) : referral ? (
        <Button className={classes.loginButton}>Become a gotmyer!</Button>
    ) : null;
};

export default LoginAndUserMenu;
